import { PropsWithChildren, useEffect, useState } from 'react';

import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import useEventListeners from '@/hooks/utils/useEventListeners';
import { MF_SET, REGISTER_MF } from '@/utils/constants/tracking';
import isEmpty from '@/utils/isEmpty';

export type DataLayerProps = PropsWithChildren;

export type RegisteredMFsType = { [key: string]: boolean };

export const getSiteEdition = (locale: string | undefined, region: string) => {
    if (locale) {
        const langCode = locale.split('-')[0];
        return `${langCode}|${region}`;
    }
    return `en|${region}`;
};

export default function DataLayer({ children }: DataLayerProps) {
    const [ready, setReady] = useState(false);
    const [registeredMFs, setRegistered] = useState({} as RegisteredMFsType);

    const updateMFE = {
        name: MF_SET,
        callback() {
            setReady(true);
        },
    };
    useEventListeners(updateMFE, []);

    useEffect(() => {
        const dataLayerCallBacksRegistered = () => {
            const ce = new CustomEvent(`${MF_SET}`);
            setTimeout(() => {
                window.dispatchEvent(ce);
            }, 500);
        };

        const registerMFs = (e: Event) => {
            const { detail } = e as CustomEvent<{ name?: string; ready?: boolean }>;
            const name = detail?.name as string;
            const ready = detail?.ready as boolean;
            if (name) {
                setRegistered((oldState) => ({ ...oldState, [name]: ready }));
            }
        };

        window.addEventListener('dataLayerCallBacksRegistered', dataLayerCallBacksRegistered);
        window.addEventListener(REGISTER_MF, registerMFs);

        if (!ready) {
            dataLayerCallBacksRegistered();
        }

        return () => {
            window.removeEventListener(
                'dataLayerCallBacksRegistered',
                dataLayerCallBacksRegistered,
            );
            window.removeEventListener(REGISTER_MF, registerMFs);
        };
    }, [ready]);

    const allReady =
        ready && (isEmpty(registeredMFs) || Object.values(registeredMFs).every(Boolean));

    useEffect(() => {
        if (allReady) {
            BootstrapSignals.mark(BootstrapSignalKeys.MFES_READY);
        }
    }, [allReady]);

    return <>{children}</>;
}
